import { isActionableKind } from './kinds';

/**
 * Current state is the newest decision per item — `approval_decisions` is
 * append-only so that a re-review after "Needs changes" keeps what was said the
 * first time, and so an Undo is a real event rather than an erasure.
 */
export interface DecisionLike {
  item_id: string;
  verdict: string;
  created_at: string;
}

export interface ItemLike {
  id: string;
  item_kind: string;
}

export function latestDecisionByItem<T extends DecisionLike>(decisions: T[]): Map<string, T> {
  const latest = new Map<string, T>();
  for (const decision of decisions) {
    const current = latest.get(decision.item_id);
    if (!current || decision.created_at > current.created_at) {
      latest.set(decision.item_id, decision);
    }
  }
  return latest;
}

export interface ApprovalProgress {
  /** Items the reviewer can actually act on. Context rows are excluded. */
  actionable: number;
  decided: number;
  approved: number;
  changesRequested: number;
  declined: number;
  answered: number;
  remaining: number;
  isComplete: boolean;
}

export function computeProgress(items: ItemLike[], decisions: DecisionLike[]): ApprovalProgress {
  const latest = latestDecisionByItem(decisions);
  const actionableItems = items.filter((item) => isActionableKind(item.item_kind));

  let approved = 0;
  let changesRequested = 0;
  let declined = 0;
  let answered = 0;
  let decided = 0;

  for (const item of actionableItems) {
    const verdict = latest.get(item.id)?.verdict;
    if (!verdict) continue;
    decided += 1;
    if (verdict === 'approved') approved += 1;
    else if (verdict === 'changes_requested') changesRequested += 1;
    else if (verdict === 'declined') declined += 1;
    else if (verdict === 'answered') answered += 1;
  }

  return {
    actionable: actionableItems.length,
    decided,
    approved,
    changesRequested,
    declined,
    answered,
    remaining: actionableItems.length - decided,
    isComplete: actionableItems.length > 0 && decided === actionableItems.length,
  };
}

/**
 * The one status a request reports to the operator list. Ordered by what the
 * operator has to do next, not by lifecycle position: anything the client
 * pushed back on outranks anything merely waiting.
 */
export type ApprovalRequestState =
  'draft' | 'closed' | 'changes_requested' | 'waiting' | 'complete';

export function requestState(status: string, progress: ApprovalProgress): ApprovalRequestState {
  if (status === 'draft') return 'draft';
  if (status === 'closed') return 'closed';
  if (progress.changesRequested > 0) return 'changes_requested';
  if (progress.isComplete) return 'complete';
  return 'waiting';
}

export const REQUEST_STATE_LABEL: Record<ApprovalRequestState, string> = {
  draft: 'Draft',
  waiting: 'Waiting on client',
  changes_requested: 'Needs changes',
  complete: 'Approved',
  closed: 'Closed',
};
